import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, FileStack, Trash2, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["DIHANTAR", "DALAM_SEMAKAN", "PERLU_MAKLUMAT_TAMBAHAN", "DILULUSKAN", "DITOLAK"];

export default function Permohonan() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("active");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = tab === "deleted" ? "/applications/deleted" : "/applications";
      const params = tab === "deleted"
        ? { search: search || undefined, limit: 50 }
        : { search: search || undefined, status: status !== "all" ? status : undefined, limit: 50 };
      const res = await api.get(url, { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status, tab]);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/applications/${deleteTarget}`, { data: { reason: deleteReason || null } });
      toast.success("Permohonan dipindahkan ke rekod dipadam");
      setDeleteTarget(null);
      setDeleteReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const restore = async (id) => {
    try {
      await api.post(`/applications/${id}/restore`);
      toast.success("Permohonan dipulihkan");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-permohonan-page">
      <h1 className="font-display text-2xl font-bold text-piks-black mb-1">Permohonan Keahlian</h1>
      <p className="text-sm text-piks-muted mb-4">{total} permohonan {tab === "deleted" ? "dipadam" : "direkodkan"}.</p>

      <div className="flex gap-2 mb-6 border-b border-piks-border">
        <button
          onClick={() => setTab("active")}
          data-testid="permohonan-tab-active"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "active" ? "border-piks-black text-piks-black" : "border-transparent text-piks-muted hover:text-piks-black"}`}
        >
          Senarai Aktif
        </button>
        <button
          onClick={() => setTab("deleted")}
          data-testid="permohonan-tab-deleted"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "deleted" ? "border-piks-red text-piks-red" : "border-transparent text-piks-muted hover:text-piks-red"}`}
        >
          Permohonan Dipadam
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 flex-1 max-w-sm">
          <Input data-testid="permohonan-search-input" placeholder="Cari nama, emel, syarikat" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-white" />
        </form>
        {tab === "active" && (
          <Select value={status} onValueChange={(v) => { setStatus(v); setSearchParams(v === "all" ? {} : { status: v }); }}>
            <SelectTrigger data-testid="permohonan-status-filter" className="w-48 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <p className="text-piks-muted text-sm">Memuatkan...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-piks-muted bg-white rounded-xl border border-piks-border" data-testid="permohonan-empty-state">
          <FileStack className="w-10 h-10 mx-auto mb-3 opacity-40" /> {tab === "deleted" ? "Tiada permohonan dipadam." : "Tiada permohonan dijumpai."}
        </div>
      ) : (
        <>
          <div className="hidden sm:block bg-white rounded-xl border border-piks-border overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr className="border-b border-piks-border text-left text-xs text-piks-muted uppercase tracking-wide">
                  <th className="p-4">Nama</th><th className="p-4">Emel</th><th className="p-4">Syarikat</th>
                  {tab === "active" ? <th className="p-4">Status</th> : <th className="p-4">Sebab Dipadam</th>}
                  <th className="p-4">{tab === "active" ? "Tarikh Hantar" : "Tarikh Dipadam"}</th><th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} className="border-b border-piks-border last:border-0 hover:bg-piks-ivory/50" data-testid={`permohonan-row-${a.id}`}>
                    <td className="p-4 font-medium text-piks-black">{a.full_name}</td>
                    <td className="p-4 text-piks-muted">{a.email}</td>
                    <td className="p-4 text-piks-muted">{a.company_name || "-"}</td>
                    {tab === "active" ? <td className="p-4"><StatusBadge status={a.status} /></td> : <td className="p-4 text-piks-muted">{a.delete_reason || "-"}</td>}
                    <td className="p-4 text-piks-muted">{(tab === "active" ? a.submitted_at : a.deleted_at) ? new Date(tab === "active" ? a.submitted_at : a.deleted_at).toLocaleDateString("ms-MY") : "-"}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-3 justify-end">
                        <Link to={`/admin/permohonan/${a.id}`} className="text-piks-black font-semibold hover:text-piks-red" data-testid={`permohonan-view-${a.id}`}>Lihat</Link>
                        {tab === "active" ? (
                          <button onClick={() => setDeleteTarget(a.id)} className="text-piks-muted hover:text-piks-red" data-testid={`permohonan-delete-${a.id}`} title="Padam Permohonan">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <button onClick={() => restore(a.id)} className="text-piks-muted hover:text-piks-black" data-testid={`permohonan-restore-${a.id}`} title="Pulihkan">
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-3">
            {items.map((a) => (
              <div key={a.id} className="p-4 rounded-xl bg-white border border-piks-border" data-testid={`permohonan-card-${a.id}`}>
                <Link to={`/admin/permohonan/${a.id}`} className="block">
                  <div className="flex justify-between items-start mb-2 gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-piks-black truncate">{a.full_name}</p>
                      <p className="text-xs text-piks-muted truncate">{a.email}</p>
                    </div>
                    {tab === "active" ? <StatusBadge status={a.status} /> : null}
                  </div>
                  <div className="flex justify-between text-xs text-piks-muted">
                    <span className="truncate">{a.company_name || "-"}</span>
                    <span className="shrink-0 ml-2">{(tab === "active" ? a.submitted_at : a.deleted_at) ? new Date(tab === "active" ? a.submitted_at : a.deleted_at).toLocaleDateString("ms-MY") : "-"}</span>
                  </div>
                </Link>
                {tab === "deleted" && (
                  <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => restore(a.id)} data-testid={`permohonan-restore-mobile-${a.id}`}>
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Pulihkan
                  </Button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent data-testid="permohonan-list-delete-dialog">
          <DialogHeader>
            <DialogTitle>PADAM PERMOHONAN?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-piks-muted">
            Permohonan ini akan dipindahkan ke rekod dipadam dan tidak lagi muncul dalam senarai permohonan aktif.
          </p>
          <div>
            <Label>Sebab Dipadam (pilihan)</Label>
            <Textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} className="mt-1.5" data-testid="permohonan-list-delete-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="permohonan-list-delete-cancel-btn">BATAL</Button>
            <Button onClick={confirmDelete} disabled={deleting} className="bg-piks-red hover:bg-piks-red/90" data-testid="permohonan-list-delete-confirm-btn">
              {deleting ? "Memadam..." : "PADAM PERMOHONAN"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
